import { useMemo } from 'react';

import type { UIFeatures } from '../types';
import { uiFeatureAccessSymbol } from '../types';
import { useShallowMemo } from '../util';

type UseStrictFeatureSettings = <T extends UIFeatures>(features: T) => T;

/**
 * @deprecated `@ms/yammer-web-ui-features` is a monolithic package which adds bloat from all feature access settings
 * to a single bootstrap API call (FeatureAccessSettingsClients) and JS assets. DO NOT add new code to this package.
 * Your team is responsible for migrating fields and code out of this package as you move your features to Relay, where
 * your feature should request the necessary `viewerCanXX` fields in its own colocated fragment. Note that we may also
 * need to design a new scalable permissions-check solution for features which require additional logic to check access.
 */
export const useStrictFeatureSettingsProduction: UseStrictFeatureSettings = useShallowMemo;

/**
 * @deprecated `@ms/yammer-web-ui-features` is a monolithic package which adds bloat from all feature access settings
 * to a single bootstrap API call (FeatureAccessSettingsClients) and JS assets. DO NOT add new code to this package.
 * Your team is responsible for migrating fields and code out of this package as you move your features to Relay, where
 * your feature should request the necessary `viewerCanXX` fields in its own colocated fragment. Note that we may also
 * need to design a new scalable permissions-check solution for features which require additional logic to check access.
 */
export const useStrictFeatureSettingsDevelopment: UseStrictFeatureSettings = (features) => {
  const target = useShallowMemo(features);

  return useMemo(
    () =>
      new Proxy(target, {
        get: (target, prop, receiver) => {
          const shouldGetProp =
            Reflect.has(target, prop) ||
            prop === '$$typeof' ||
            (typeof prop === 'symbol' && prop !== uiFeatureAccessSymbol);

          if (shouldGetProp) {
            return Reflect.get(target, prop, receiver);
          }

          const consoleTraceStyles = [
            'background: #2259A1',
            'border-radius: 0.5em',
            'color: white',
            'font-weight: bold',
            'padding: 3px 0.5em',
          ].join(';');

          // React Developer Tools overrides `console.trace`, appending the render tree to the message.
          console.trace('%c[[UIFeatures]]', consoleTraceStyles, target);
          throw new ReferenceError(`[[UIFeatures]].${String(prop)} is not initialized in <UIFeaturesProvider>`);
        },
      }),
    [target]
  );
};

/**
 * @deprecated `@ms/yammer-web-ui-features` is a monolithic package which adds bloat from all feature access settings
 * to a single bootstrap API call (FeatureAccessSettingsClients) and JS assets. DO NOT add new code to this package.
 * Your team is responsible for migrating fields and code out of this package as you move your features to Relay, where
 * your feature should request the necessary `viewerCanXX` fields in its own colocated fragment. Note that we may also
 * need to design a new scalable permissions-check solution for features which require additional logic to check access.
 */
export const useStrictFeatureSettings: UseStrictFeatureSettings =
  process.env.NODE_ENV === 'production' ? useStrictFeatureSettingsProduction : useStrictFeatureSettingsDevelopment;
